// Sentry error monitoring service for RMS-client
//
// This service provides a singleton for Sentry integration and error reporting.
// It should be initialized once at app startup (e.g., in startup-service or index.tsx)
// and used throughout the project as a singleton.

import * as Sentry from '@sentry/react';

import config from '../config';
import currentPortalStore from '../stores/current-portal-store';
import DashboardTranslationStore from '../dashboard-translation/stores/dashboard-translations-store';

class ErrorReportingService {
    private initialized = false;

    /**
     * Initialize Sentry for error reporting.
     * Should be called once at app startup.
     */
    initialize(): void {
        if (this.initialized) {
            return;
        }
        const dsn = config.sentryDsn;
        if (!dsn) {
            console.warn('[ErrorReportingService] Sentry DSN not found. Error tracking is disabled.');
            return;
        }
        // Use config.sentryEnv, fallback to config.env, then to 'local'
        const environment = config.sentryEnv || config.env || 'local';
        Sentry.init({
            dsn,
            environment,
            beforeSend(event) {
                // Filter out ResizeObserver loop errors as they are not actionable
                if (event.exception) {
                    const error = event.exception.values?.[0];
                    if (error?.value?.includes('ResizeObserver loop completed with undelivered notifications')) {
                        return null; // Don't send this error to Sentry
                    }
                }
                return event;
            },
            ignoreErrors: [
                // Ignore ResizeObserver loop errors
                'ResizeObserver loop completed with undelivered notifications',
            ],
        });
        this.initialized = true;
    }

    /**
     * Set Sentry context tags for error reports.
     * @param tags - Key-value pairs to set as Sentry tags.
     */
    setContext(tags: Record<string, any>): void {
        if (!config.sentryDsn) {
            return;
        }
        try {
            Sentry.setTags(tags);
        } catch (error) {
            console.error('[ErrorReportingService] Sentry setTags failed:', error);
        }
    }

    /**
     * Capture and report a custom exception to Sentry.
     * This method will never crash the app.
     * @param error - The error/exception to report
     * @param context - Optional additional context/tags
     */
    captureException(error: Error, context?: Record<string, any>): void {
        if (!config.sentryDsn) {
            return;
        }
        try {
            // Add user_id (merchant_id) tag
            const userId = currentPortalStore.currentPortal?.id;
            // Add locale/language tag
            const locale = DashboardTranslationStore.locale;

            Sentry.withScope(scope => {
                if (userId !== undefined) {
                    scope.setTag('user_id', String(userId));
                }

                if (locale) {
                    scope.setTag('locale', locale);
                }

                // Add any additional context tags
                if (context) {
                    Object.entries(context).forEach(([key, value]) => {
                        scope.setTag(key, String(value));
                    });
                }

                Sentry.captureException(error);
            });
        } catch (err) {
            console.error('[ErrorReportingService] Sentry captureException failed:', err);
        }
    }
}

export default ErrorReportingService;
